import { useCallback, useEffect, useState, useMemo } from "react";
import {
    FormControl,
    TextField,
    Button,
    InputLabel,
    Select,
    MenuItem,
    CircularProgress,
    Checkbox,
} from "@mui/material";
import { DateTimeField, LocalizationProvider } from "@mui/x-date-pickers";
import makeStyles from "@mui/styles/makeStyles";
import VisibleSpansDeviceConfig from "./VisibleSpansDeviceConfig";
import { useAppSelector, useAppDispatch } from "./store/hooks";
import {
    selectCurrentDeviceTypeBeingEdited,
    selectCurrentDeviceBeingEdited,
    setCurrentDeviceBeingEdited,
    selectCurrentPosition,
    selectActiveStructure,
    stopEditingDevice,
    submitEdgeDeviceChange,
    submitWeatherSensorChange,
    loadLatestDeviceDataDataset,
    selectDeviceUpdateStatus,
    selectExistingDeviceIdBeingEdited,
    selectDeviceUpdateError,
} from "./store/reducers/deviceLinkingAppStoreSlice";
import ErrorText from "./ErrorText";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import { Device, WeatherSensor } from "../types";
import { telemetryService } from "../../../../services/TelemetryService";
import { showGlobalErrorToast } from "../../../../utils/GlobalToast";
import { el } from "date-fns/locale";

dayjs.extend(utc);

const useStyles = makeStyles(() => ({
    formControlContainer: {
        display: "flex",
        flexDirection: "column",
    },
    formButtons: {
        alignSelf: "flex-end",
    },
}));

const EDGE_DEVICE_TYPE_OPTIONS = [{ value: "lux", label: "LUX" }];
const LIDAR_TYPE_OPTIONS = [
    { value: "vlp-32", label: "VLP-32" },
    { value: "os1-128", label: "OS1-128" },
];
// Each value must match a member of the masterdata weather_sensor_device_type enum
// exactly. It is submitted verbatim, so a mismatch is rejected by the API, not here.
export const WEATHER_SENSOR_TYPE_OPTIONS = [
    { value: "zentra", label: "Zentra" },
    { value: "young", label: "Young" },
];

const CALL_SCHEDULE_INTERVAL_MINUTES_MAX = 180;

export const CARDINAL_DIRECTIONS = [
    { value: "0", label: "N" },
    { value: "45", label: "NE" },
    { value: "90", label: "E" },
    { value: "135", label: "SE" },
    { value: "180", label: "S" },
    { value: "225", label: "SW" },
    { value: "270", label: "W" },
    { value: "315", label: "NW" },
];

type AddOrEditDeviceFormProps = {
    className?: string;
};

export default function AddOrEditDeviceForm(props: AddOrEditDeviceFormProps): JSX.Element {
    const dispatch = useAppDispatch();
    const classes = useStyles();

    const currentPosition = useAppSelector(selectCurrentPosition);
    const currentActiveStructure = useAppSelector(selectActiveStructure);
    const currentDeviceTypeBeingEdited = useAppSelector(selectCurrentDeviceTypeBeingEdited);
    const deviceData = useAppSelector(selectCurrentDeviceBeingEdited);
    const existingDeviceId = useAppSelector(selectExistingDeviceIdBeingEdited);
    const deviceUpdateStatus = useAppSelector(selectDeviceUpdateStatus);
    const deviceUpdateError = useAppSelector(selectDeviceUpdateError);

    // Install Date validation: must not be the default (current) date/time
    const edgeDeviceInstallDateError = useMemo(() => {
        if (currentDeviceTypeBeingEdited === "edge-device" && deviceData) {
            const edgeDeviceData = deviceData as Partial<Device>;
            const installDate = edgeDeviceData.install_date;
            if (!installDate) return true;
            // Compare to current date/time, rounded to the nearest minute for practical purposes
            const now = dayjs.utc();
            const install = dayjs.utc(installDate);
            // If the difference is less than 2 minutes, treat as unchanged
            return Math.abs(now.diff(install, "minute")) < 2;
        }
        return false;
    }, [currentDeviceTypeBeingEdited, deviceData]);
    const edgeDeviceInstallDateErrorMessage = useMemo(
        () =>
            edgeDeviceInstallDateError
                ? "Install Date must be set to the actual installation time (not the default current time)"
                : "",
        [edgeDeviceInstallDateError]
    );

    // LiDAR Serial Number required validation
    const edgeDeviceLidarSerialError = useMemo(() => {
        if (currentDeviceTypeBeingEdited === "edge-device" && deviceData) {
            const edgeDeviceData = deviceData as Partial<Device>;
            const lidarSerial = edgeDeviceData.lidar_serial_number;
            return lidarSerial === undefined || lidarSerial === null || lidarSerial === "";
        }
        return false;
    }, [currentDeviceTypeBeingEdited, deviceData]);
    const edgeDeviceLidarSerialErrorMessage = useMemo(
        () => (edgeDeviceLidarSerialError ? "LiDAR Serial Number is required" : ""),
        [edgeDeviceLidarSerialError]
    );

    // Edge Device Serial Number validation: must be present and a string of length 10
    const edgeDeviceSerialError = useMemo(() => {
        if (currentDeviceTypeBeingEdited === "edge-device" && deviceData) {
            const edgeDeviceData = deviceData as Partial<Device>;
            const serial = edgeDeviceData.cloudgate_serial_no;
            if (serial === undefined || serial === null || serial === "") return true;
            return String(serial).length !== 10;
        }
        return false;
    }, [currentDeviceTypeBeingEdited, deviceData]);
    const edgeDeviceSerialErrorMessage = useMemo(() => {
        if (currentDeviceTypeBeingEdited === "edge-device" && deviceData) {
            const edgeDeviceData = deviceData as Partial<Device>;
            const serial = edgeDeviceData.cloudgate_serial_no;
            if (serial === undefined || serial === null || serial === "") {
                return "Serial Number is required";
            }
            if (String(serial).length !== 10) {
                return "Serial Number must be exactly 10 characters (e.g. KM4AP5A406)";
            }
        }
        return "";
    }, [currentDeviceTypeBeingEdited, deviceData, edgeDeviceSerialError]);

    const [skipMasterdataSync, setSkipMasterdataSync] = useState(false);

    // Handle Skip sync checkbox with confirmation
    const handleSkipSyncChange = (checked: boolean) => {
        if (checked) {
            const confirmed = window.confirm(
                "⚠️ WARNING: Skipping Masterdata Sync\n\n" +
                    "This option should ONLY be used in specific explicit scenarios with permission from engineering teams.\n\n" +
                    "If you are experiencing issues with Device Linking, please reach out to the engineering team for assistance.\n\n" +
                    "Skipping sync may cause:\n" +
                    "• Data inconsistencies between systems\n" +
                    "• Missing device configurations in production\n" +
                    "• Downstream processing failures\n\n" +
                    "Are you absolutely sure you need to skip Masterdata sync?"
            );

            if (confirmed) {
                // Ask for a second confirmation for extra safety
                const doubleConfirmed = window.confirm(
                    "Please confirm once more:\n\n" +
                        "I understand the risks and have a valid reason to skip Masterdata sync."
                );

                if (doubleConfirmed) {
                    setSkipMasterdataSync(true);
                }
            }
        } else {
            // Allow unchecking without confirmation
            setSkipMasterdataSync(false);
        }
    };

    // Set default value for call schedule interval if not set
    useEffect(() => {
        if (
            currentDeviceTypeBeingEdited === "edge-device" &&
            deviceData &&
            ((deviceData as Partial<Device>).call_schedule === undefined ||
                (deviceData as Partial<Device>).call_schedule?.interval === undefined)
        ) {
            // Only add call_schedule if deviceData is a Device
            const device = deviceData as Partial<Device>;
            dispatch(
                setCurrentDeviceBeingEdited({
                    ...device,
                    call_schedule: { interval: 60, offset: 0 },
                })
            );
        }
    }, [currentDeviceTypeBeingEdited, deviceData, dispatch]);

    // Required field validation for edge device
    const edgeDeviceSiteIdError = useMemo(() => {
        if (currentDeviceTypeBeingEdited === "edge-device" && deviceData) {
            const edgeDeviceData = deviceData as Partial<Device>;
            const siteId = edgeDeviceData.site_id;
            return siteId === undefined || siteId === null || siteId === "";
        }
        return false;
    }, [currentDeviceTypeBeingEdited, deviceData]);
    const edgeDeviceSiteIdErrorMessage = useMemo(
        () => (edgeDeviceSiteIdError ? "Site Number is required" : ""),
        [edgeDeviceSiteIdError]
    );

    const edgeDeviceCallScheduleError = useMemo(() => {
        if (currentDeviceTypeBeingEdited === "edge-device" && deviceData) {
            const edgeDeviceData = deviceData as Partial<Device>;
            const interval = edgeDeviceData.call_schedule?.interval;
            return (
                !edgeDeviceData.call_schedule ||
                interval === undefined ||
                interval === null ||
                (typeof interval === "string" && (interval as string).trim() === "")
            );
        }
        return false;
    }, [currentDeviceTypeBeingEdited, deviceData]);
    // Restore storeChangeInDeviceBeingEdited function
    // Don't memoize this function to avoid stale closure issues with deviceData
    const storeChangeInDeviceBeingEdited = (field: string, newValue: unknown) => {
        if (!deviceData) {
            return;
        }
        const updatedData = { ...deviceData, [field]: newValue };
        dispatch(setCurrentDeviceBeingEdited(updatedData));
    };
    const edgeDeviceCallScheduleErrorMessage = useMemo(
        () => (edgeDeviceCallScheduleError ? "Call Schedule is required" : ""),
        [edgeDeviceCallScheduleError]
    );

    const edgeDeviceTypeError = useMemo(() => {
        if (currentDeviceTypeBeingEdited === "edge-device" && deviceData) {
            const edgeDeviceData = deviceData as Partial<Device>;
            return !edgeDeviceData.device_type;
        }
        return false;
    }, [currentDeviceTypeBeingEdited, deviceData]);
    const edgeDeviceTypeErrorMessage = useMemo(
        () => (edgeDeviceTypeError ? "Edge Device Type is required" : ""),
        [edgeDeviceTypeError]
    );

    const edgeDeviceLidarTypeError = useMemo(() => {
        if (currentDeviceTypeBeingEdited === "edge-device" && deviceData) {
            const edgeDeviceData = deviceData as Partial<Device>;
            return !edgeDeviceData.lidar_type;
        }
        return false;
    }, [currentDeviceTypeBeingEdited, deviceData]);
    const edgeDeviceLidarTypeErrorMessage = useMemo(
        () => (edgeDeviceLidarTypeError ? "Lidar Type is required" : ""),
        [edgeDeviceLidarTypeError]
    );

    const edgeDeviceHeadingError = useMemo(() => {
        if (currentDeviceTypeBeingEdited === "edge-device" && deviceData) {
            const edgeDeviceData = deviceData as Partial<Device>;
            const heading = edgeDeviceData.approx_angle;
            return heading === undefined || heading === null;
        }
        return false;
    }, [currentDeviceTypeBeingEdited, deviceData]);
    const edgeDeviceHeadingErrorMessage = useMemo(
        () => (edgeDeviceHeadingError ? "Device Heading is required. Click a structure on the map to set it." : ""),
        [edgeDeviceHeadingError]
    );

    // The in-progress device should always have the latest position. I think it
    // makes sense to put this logic here because we can kind of think of it as
    // a "hidden form input" that is always being updated as long as a device is
    // being edited.
    // TODO rethink for device editing
    useEffect(() => {
        // No need to update the device if it's not being edited
        if (currentPosition && currentDeviceTypeBeingEdited === "weather-sensor") {
            dispatch(
                setCurrentDeviceBeingEdited({
                    ...(deviceData as Partial<WeatherSensor>),
                    position: currentPosition,
                    structure_name: currentActiveStructure?.structure_name,
                })
            );
        } else if (currentPosition && currentDeviceTypeBeingEdited === "edge-device") {
            dispatch(setCurrentDeviceBeingEdited({ ...deviceData, position: currentPosition }));
        }
    }, [currentPosition]);

    // Store elevation for weather sensor if height_above_ground is filled out
    useEffect(() => {
        if (currentActiveStructure && currentDeviceTypeBeingEdited === "weather-sensor" && deviceData !== undefined) {
            const weatherSensorData = deviceData as Partial<WeatherSensor>;
            if (weatherSensorData.height_above_ground) {
                // Enforce max of 2 digits after decimal point, following MD schema
                const currentElevation = Number(
                    (currentActiveStructure.ground_elevation + Number(weatherSensorData.height_above_ground)).toFixed(2)
                );

                // Prevent infinite recursive calls
                if (currentElevation != weatherSensorData.elevation) {
                    dispatch(
                        setCurrentDeviceBeingEdited({
                            ...weatherSensorData,
                            position: currentPosition,
                            elevation: currentElevation,
                            structure_name: currentActiveStructure.structure_name,
                        })
                    );
                }
            }
        }
    }, [currentActiveStructure, currentDeviceTypeBeingEdited, deviceData]);

    const onSubmitEdgeDeviceForm = useCallback(async () => {
        const edgeDeviceId = (deviceData as Partial<Device>)?.edge_device_id;
        const isNewDevice = !existingDeviceId;

        const result = await dispatch(submitEdgeDeviceChange({ skipMasterdataSync: skipMasterdataSync }));

        // Check if the submission was successful
        if (submitEdgeDeviceChange.fulfilled.match(result)) {
            dispatch(loadLatestDeviceDataDataset());

            // Update telemetry edge device config with call_schedule
            const serialNo = (deviceData as Partial<Device>)?.cloudgate_serial_no;
            const callScheduleMinutes = (deviceData as Partial<Device>)?.call_schedule?.interval;

            if (serialNo && callScheduleMinutes) {
                try {
                    const configResponse = await telemetryService.getEdgeConfig(serialNo);
                    const deviceConfig = configResponse.data.device_config;
                    const callScheduleSeconds = callScheduleMinutes * 60;
                    const compressedScanIndex = deviceConfig.scan_settings.algorithm_list.indexOf("compressedScan");

                    deviceConfig.server_settings.server_contact_interval = callScheduleSeconds;
                    deviceConfig.scan_settings.compressed_scan_interval = callScheduleSeconds;
                    if (compressedScanIndex !== -1) {
                        deviceConfig.scan_settings.scan_interval[compressedScanIndex] = callScheduleSeconds;
                    }

                    await telemetryService.saveEdgeConfig(serialNo, deviceConfig);
                } catch (error) {
                    showGlobalErrorToast(
                        `Device saved successfully, but failed to update Edge Device Config call schedule: ${error}`
                    );
                }
            }

            // Show success message
            const action = isNewDevice ? "added" : "updated";
            alert(`Edge device ${edgeDeviceId} has been ${action} successfully!`);
        }
    }, [skipMasterdataSync, deviceData, existingDeviceId, dispatch]);

    const onSubmitWeatherSensorForm = useCallback(async () => {
        const sensorSn = (deviceData as Partial<WeatherSensor>)?.device_sn;
        const isNewSensor = !existingDeviceId;

        const result = await dispatch(submitWeatherSensorChange({ skipMasterdataSync: skipMasterdataSync }));

        // Check if the submission was successful
        if (submitWeatherSensorChange.fulfilled.match(result)) {
            dispatch(loadLatestDeviceDataDataset());

            // Show success message
            const action = isNewSensor ? "added" : "updated";
            alert(`Weather sensor ${sensorSn} has been ${action} successfully!`);
        }
    }, [skipMasterdataSync, deviceData, existingDeviceId, dispatch]);

    // Edge Device ID validation: must be present and a 4-digit integer
    const edgeDeviceIdError = useMemo(() => {
        if (currentDeviceTypeBeingEdited === "edge-device" && deviceData) {
            const edgeDeviceData = deviceData as Partial<Device>;
            const id = edgeDeviceData.edge_device_id;
            if (id === undefined || id === null || (typeof id === "string" && (id as string).trim() === ""))
                return true;
            // Accept string or number input
            const idStr = String(id);
            return !/^\d{4}$/.test(idStr);
        }
        return false;
    }, [currentDeviceTypeBeingEdited, deviceData]);
    const edgeDeviceIdErrorMessage = useMemo(() => {
        if (currentDeviceTypeBeingEdited === "edge-device" && deviceData) {
            const edgeDeviceData = deviceData as Partial<Device>;
            const id = edgeDeviceData.edge_device_id;
            if (id === undefined || id === null || (typeof id === "string" && (id as string).trim() === "")) {
                return "Edge Device ID is required";
            }
            const idStr = String(id);
            if (!/^\d{4}$/.test(idStr)) {
                return "Edge Device ID must be a 4-digit integer (e.g. 1234)";
            }
        }
        return "";
    }, [currentDeviceTypeBeingEdited, deviceData, edgeDeviceIdError]);

    // Weather Sensor Serial Number validation: must be present
    const weatherSensorSerialError = useMemo(() => {
        if (currentDeviceTypeBeingEdited === "weather-sensor" && deviceData) {
            const weatherSensorData = deviceData as Partial<WeatherSensor>;
            const serial = weatherSensorData.device_sn;
            return serial === undefined || serial === null || serial === "";
        }
        return false;
    }, [currentDeviceTypeBeingEdited, deviceData]);
    const weatherSensorSerialErrorMessage = useMemo(
        () => (weatherSensorSerialError ? "Serial Number is required" : ""),
        [weatherSensorSerialError]
    );

    // Weather Sensor Site Number validation: must be present
    const weatherSensorSiteIdError = useMemo(() => {
        if (currentDeviceTypeBeingEdited === "weather-sensor" && deviceData) {
            const weatherSensorData = deviceData as Partial<WeatherSensor>;
            const siteId = weatherSensorData.site_id;
            return siteId === undefined || siteId === null || siteId === "";
        }
        return false;
    }, [currentDeviceTypeBeingEdited, deviceData]);
    const weatherSensorSiteIdErrorMessage = useMemo(
        () => (weatherSensorSiteIdError ? "Site Number is required" : ""),
        [weatherSensorSiteIdError]
    );

    // Weather Sensor Device Type validation: must be present
    const weatherSensorTypeError = useMemo(() => {
        if (currentDeviceTypeBeingEdited === "weather-sensor" && deviceData) {
            const weatherSensorData = deviceData as Partial<WeatherSensor>;
            return !weatherSensorData.device_type;
        }
        return false;
    }, [currentDeviceTypeBeingEdited, deviceData]);
    const weatherSensorTypeErrorMessage = useMemo(
        () => (weatherSensorTypeError ? "Device Type is required" : ""),
        [weatherSensorTypeError]
    );

    // Weather Sensor Mounting Arm Heading validation: must be present
    const weatherSensorMountingArmHeadingError = useMemo(() => {
        if (currentDeviceTypeBeingEdited === "weather-sensor" && deviceData) {
            const weatherSensorData = deviceData as Partial<WeatherSensor>;
            const heading = weatherSensorData.mounting_arm_orientation_heading;
            // Check for undefined, null (heading is a number, 0 is a valid value for North)
            return heading === undefined || heading === null;
        }
        return false;
    }, [currentDeviceTypeBeingEdited, deviceData]);
    const weatherSensorMountingArmHeadingErrorMessage = useMemo(
        () =>
            weatherSensorMountingArmHeadingError
                ? "Mounting Arm Heading is required and must be a valid direction (N, NE, E, SE, S, SW, W, NW)"
                : "",
        [weatherSensorMountingArmHeadingError]
    );

    // Weather Sensor Install Date validation: must not be the default (current) date/time
    const weatherSensorInstallDateError = useMemo(() => {
        if (currentDeviceTypeBeingEdited === "weather-sensor" && deviceData) {
            const weatherSensorData = deviceData as Partial<WeatherSensor>;
            const installDate = weatherSensorData.start_dttm;
            if (!installDate) return true;
            const now = dayjs.utc();
            const install = dayjs.utc(installDate);
            return Math.abs(now.diff(install, "minute")) < 2;
        }
        return false;
    }, [currentDeviceTypeBeingEdited, deviceData]);
    const weatherSensorInstallDateErrorMessage = useMemo(
        () =>
            weatherSensorInstallDateError
                ? "Install Date must be set to the actual installation time (not the default current time)"
                : "",
        [weatherSensorInstallDateError]
    );

    // Weather Sensor Install Height validation: must be present
    const weatherSensorInstallHeightError = useMemo(() => {
        if (currentDeviceTypeBeingEdited === "weather-sensor" && deviceData) {
            const weatherSensorData = deviceData as Partial<WeatherSensor>;
            const height = weatherSensorData.height_above_ground;
            if (height === undefined || height === null) return true;
            if (typeof height === "string") {
                if ((height as string).trim() === "") return true;
            }
            return false;
        }
        return false;
    }, [currentDeviceTypeBeingEdited, deviceData]);
    const weatherSensorInstallHeightErrorMessage = useMemo(
        () => (weatherSensorInstallHeightError ? "Install Height is required" : ""),
        [weatherSensorInstallHeightError]
    );

    // Weather Sensor End Date validation: must be after start date if provided
    const weatherSensorEndDateError = useMemo(() => {
        if (currentDeviceTypeBeingEdited === "weather-sensor" && deviceData) {
            const weatherSensorData = deviceData as Partial<WeatherSensor>;
            const startDttm = weatherSensorData.start_dttm;
            const endDttm = weatherSensorData.end_dttm;

            // Only validate if end_dttm is provided
            if (!endDttm) return false;
            if (!startDttm) return false;

            const start = dayjs.utc(startDttm);
            const end = dayjs.utc(endDttm);

            // End date must be after start date
            return end.isBefore(start) || end.isSame(start);
        }
        return false;
    }, [currentDeviceTypeBeingEdited, deviceData]);
    const weatherSensorEndDateErrorMessage = useMemo(
        () => (weatherSensorEndDateError ? "End Date must be after Install Date" : ""),
        [weatherSensorEndDateError]
    );

    // Ad-hoc validations for call schedule after issue using 3600 as interval
    // TODO: If we have more of these, we should consider a more general solution
    const callScheduleIntervalError = useMemo(() => {
        if (currentDeviceTypeBeingEdited == "edge-device" && deviceData) {
            const edgeDeviceData = deviceData as Partial<Device>;
            return (
                (edgeDeviceData.call_schedule?.interval &&
                    edgeDeviceData.call_schedule?.interval > CALL_SCHEDULE_INTERVAL_MINUTES_MAX) ||
                false
            );
        }
    }, [currentDeviceTypeBeingEdited, deviceData]);
    const callScheduleIntervalErrorMessage = useMemo(
        () =>
            callScheduleIntervalError
                ? `Call Schedule interval too large, expecting schedule to be less than ${CALL_SCHEDULE_INTERVAL_MINUTES_MAX}`
                : "",
        [callScheduleIntervalError]
    );

    // Weather Sensor Mounting Arm Length warning: show if not 0.91
    const weatherSensorMountingArmLengthWarning = useMemo(() => {
        if (currentDeviceTypeBeingEdited === "weather-sensor") {
            const weatherSensorData = deviceData as Partial<WeatherSensor>;
            const length = weatherSensorData.mounting_arm_length;
            // Accept both string and number types
            if (length === undefined || length === null) return false;
            const numLength = typeof length === "string" ? parseFloat(length) : length;
            return numLength !== 0.91;
        }
        return false;
    }, [currentDeviceTypeBeingEdited, deviceData]);

    // Weather Sensor Serial Number format warning: show if not 'A41' + 6 digits
    const weatherSensorSerialFormatWarning = useMemo(() => {
        if (currentDeviceTypeBeingEdited === "weather-sensor") {
            const weatherSensorData = deviceData as Partial<WeatherSensor>;
            const serial = weatherSensorData.device_sn;
            if (typeof serial !== "string" || serial.length !== 8) return true;
            // Must start with 'A41' and followed by 5 digits
            return !/^A41\d{5}$/.test(serial);
        }
        return false;
    }, [currentDeviceTypeBeingEdited, deviceData]);

    // Structure not set warning: show if no active structure is selected
    const noStructureWarning = useMemo(() => {
        return !currentActiveStructure;
    }, [currentActiveStructure]);

    if (currentDeviceTypeBeingEdited === undefined || deviceData === undefined) {
        return <div className={classes.formControlContainer}></div>;
    }

    return (
        <div className={`${classes.formControlContainer} ${props.className || ""}`}>
            {deviceUpdateStatus === "loading" ? (
                <CircularProgress />
            ) : (
                <>
                    {currentDeviceTypeBeingEdited === "edge-device" && (
                        <h1> {existingDeviceId ? "#" + existingDeviceId : "New"} Edge Device Information</h1>
                    )}
                    {currentDeviceTypeBeingEdited === "weather-sensor" && (
                        <h1> {existingDeviceId ? "SN " + existingDeviceId : "New"} Weather Sensor Information</h1>
                    )}

                    <FormControl>
                        <InputLabel htmlFor="latitude">Latitude</InputLabel>
                        <TextField
                            id="latitude"
                            value={currentPosition?.latitude ?? ""}
                            error={noStructureWarning}
                            helperText={
                                noStructureWarning &&
                                "Position is not located on a structure. Change position by selecting a structure above."
                            }
                        />
                    </FormControl>
                    <FormControl>
                        <InputLabel htmlFor="longitude">Longitude</InputLabel>
                        <TextField
                            id="longitude"
                            value={currentPosition?.longitude ?? ""}
                            error={noStructureWarning}
                            helperText={
                                noStructureWarning &&
                                "Position is not located on a structure. Change position by selecting a structure above."
                            }
                        />
                    </FormControl>

                    {currentDeviceTypeBeingEdited === "edge-device" && (
                        <>
                            <FormControl>
                                <InputLabel htmlFor="edge-device-id">Edge Device ID</InputLabel>
                                <TextField
                                    id="edge-device-id"
                                    type="number"
                                    value={(deviceData as Partial<Device>)?.edge_device_id ?? ""}
                                    onChange={(event) =>
                                        storeChangeInDeviceBeingEdited("edge_device_id", event.target.value)
                                    }
                                    onWheel={(event) => event.target instanceof HTMLElement && event.target.blur()}
                                    error={edgeDeviceIdError}
                                    helperText={edgeDeviceIdErrorMessage}
                                />
                            </FormControl>
                            <FormControl>
                                <InputLabel htmlFor="edge-device-serial">Serial Number</InputLabel>
                                <TextField
                                    id="edge-device-serial"
                                    value={(deviceData as Partial<Device>)?.cloudgate_serial_no ?? ""}
                                    onChange={(event) =>
                                        storeChangeInDeviceBeingEdited("cloudgate_serial_no", event.target.value)
                                    }
                                    error={edgeDeviceSerialError}
                                    helperText={edgeDeviceSerialErrorMessage}
                                />
                            </FormControl>
                            <FormControl>
                                <InputLabel htmlFor="edge-device-site-id">Site Number</InputLabel>
                                <TextField
                                    id="edge-device-site-id"
                                    type="number"
                                    value={(deviceData as Partial<Device>)?.site_id ?? ""}
                                    onChange={(event) => storeChangeInDeviceBeingEdited("site_id", event.target.value)}
                                    onWheel={(event) => event.target instanceof HTMLElement && event.target.blur()}
                                    error={edgeDeviceSiteIdError}
                                    helperText={edgeDeviceSiteIdErrorMessage}
                                />
                            </FormControl>
                            <FormControl>
                                <InputLabel htmlFor="call-schedule-interval">Call Schedule (minutes)</InputLabel>
                                <TextField
                                    id="call-schedule-interval"
                                    type="number"
                                    value={(deviceData as Partial<Device>)?.call_schedule?.interval ?? ""}
                                    onChange={(event) =>
                                        storeChangeInDeviceBeingEdited("call_schedule", {
                                            interval: event.target.value,
                                            offset: 0,
                                        })
                                    }
                                    error={callScheduleIntervalError || edgeDeviceCallScheduleError}
                                    helperText={callScheduleIntervalErrorMessage || edgeDeviceCallScheduleErrorMessage}
                                    onWheel={(event) => event.target instanceof HTMLElement && event.target.blur()}
                                />
                            </FormControl>
                            <FormControl>
                                <InputLabel htmlFor="edge-device-type">Edge Device Type</InputLabel>
                                <Select
                                    id="edge-device-type"
                                    value={(deviceData as Partial<Device>)?.device_type ?? ""}
                                    onChange={(event) =>
                                        storeChangeInDeviceBeingEdited("device_type", event.target.value)
                                    }
                                    error={edgeDeviceTypeError}
                                >
                                    {EDGE_DEVICE_TYPE_OPTIONS.map((option) => (
                                        <MenuItem key={option.value} value={option.value}>
                                            {option.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                                {edgeDeviceTypeError && (
                                    <span style={{ color: "red", fontSize: "0.8em" }}>
                                        {edgeDeviceTypeErrorMessage}
                                    </span>
                                )}
                            </FormControl>
                            <FormControl>
                                <InputLabel htmlFor="lidar-type">LiDAR Type</InputLabel>
                                <Select
                                    id="lidar-type"
                                    value={(deviceData as Partial<Device>)?.lidar_type ?? ""}
                                    onChange={(event) =>
                                        storeChangeInDeviceBeingEdited("lidar_type", event.target.value)
                                    }
                                    error={edgeDeviceLidarTypeError}
                                >
                                    {LIDAR_TYPE_OPTIONS.map((option) => (
                                        <MenuItem key={option.value} value={option.value}>
                                            {option.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                                {edgeDeviceLidarTypeError && (
                                    <span style={{ color: "red", fontSize: "0.8em" }}>
                                        {edgeDeviceLidarTypeErrorMessage}
                                    </span>
                                )}
                            </FormControl>
                            <FormControl>
                                <InputLabel htmlFor="lidar-serial-number">LiDAR Serial #</InputLabel>
                                <TextField
                                    id="lidar-serial-number"
                                    value={(deviceData as Partial<Device>)?.lidar_serial_number ?? ""}
                                    onChange={(event) =>
                                        storeChangeInDeviceBeingEdited("lidar_serial_number", event.target.value)
                                    }
                                    error={edgeDeviceLidarSerialError}
                                    helperText={edgeDeviceLidarSerialErrorMessage}
                                />
                            </FormControl>
                            <FormControl>
                                <LocalizationProvider dateAdapter={AdapterDayjs}>
                                    <DateTimeField
                                        label="Install Date (In UTC)"
                                        value={dayjs.utc((deviceData as Partial<Device>)?.install_date)}
                                        onChange={(newDate) =>
                                            storeChangeInDeviceBeingEdited("install_date", newDate?.toISOString())
                                        }
                                        slotProps={{
                                            textField: {
                                                error: edgeDeviceInstallDateError,
                                                helperText: edgeDeviceInstallDateErrorMessage,
                                            },
                                        }}
                                    />
                                </LocalizationProvider>
                            </FormControl>
                            <FormControl>
                                <InputLabel htmlFor="approx-angle">Device Heading (degrees)</InputLabel>
                                <TextField
                                    id="approx-angle"
                                    type="number"
                                    value={
                                        (deviceData as Partial<Device>)?.approx_angle !== undefined
                                            ? Math.round((deviceData as Partial<Device>).approx_angle as number)
                                            : ""
                                    }
                                    onChange={(event) =>
                                        storeChangeInDeviceBeingEdited(
                                            "approx_angle",
                                            event.target.value === "" ? undefined : Number(event.target.value)
                                        )
                                    }
                                    onWheel={(event) => event.target instanceof HTMLElement && event.target.blur()}
                                    inputProps={{ min: 0, max: 360 }}
                                    error={edgeDeviceHeadingError}
                                    helperText={
                                        edgeDeviceHeadingError
                                            ? edgeDeviceHeadingErrorMessage
                                            : "0° = North, 90° = East, 180° = South, 270° = West. Click a structure on the map to auto-calculate."
                                    }
                                />
                            </FormControl>
                            <VisibleSpansDeviceConfig />
                            <div>
                                <Checkbox
                                    checked={skipMasterdataSync}
                                    onChange={(event) => handleSkipSyncChange(event.target.checked)}
                                />
                                Skip sync to masterdata (FOR INTERNAL USE ONLY)
                                {skipMasterdataSync && (
                                    <span style={{ color: "red", fontSize: "0.9em", marginLeft: "10px" }}>
                                        ⚠️ SYNC DISABLED - Data will not be sent to Masterdata
                                    </span>
                                )}
                            </div>
                            <div className={classes.formButtons}>
                                <Button onClick={() => dispatch(stopEditingDevice())}>Cancel</Button>
                                <Button
                                    onClick={() => onSubmitEdgeDeviceForm()}
                                    disabled={
                                        callScheduleIntervalError ||
                                        edgeDeviceIdError ||
                                        edgeDeviceSerialError ||
                                        edgeDeviceSiteIdError ||
                                        edgeDeviceCallScheduleError ||
                                        edgeDeviceTypeError ||
                                        edgeDeviceLidarTypeError ||
                                        edgeDeviceLidarSerialError ||
                                        edgeDeviceInstallDateError ||
                                        edgeDeviceHeadingError
                                    }
                                >
                                    Save
                                </Button>
                            </div>

                            {deviceUpdateStatus === "failed" && deviceUpdateError && (
                                <ErrorText error={deviceUpdateError} />
                            )}
                        </>
                    )}

                    {currentDeviceTypeBeingEdited === "weather-sensor" && (
                        <>
                            <FormControl>
                                <InputLabel htmlFor="structure-name">Structure</InputLabel>
                                <TextField
                                    id="structure-name"
                                    type="disabled"
                                    value={(deviceData as Partial<WeatherSensor>)?.structure_name ?? "N/A"}
                                />
                            </FormControl>
                            <FormControl>
                                <InputLabel htmlFor="weather-sensor-serial">Serial Number</InputLabel>
                                <TextField
                                    id="weather-sensor-serial"
                                    value={(deviceData as Partial<WeatherSensor>)?.device_sn ?? ""}
                                    onChange={(event) =>
                                        storeChangeInDeviceBeingEdited("device_sn", event.target.value)
                                    }
                                    error={weatherSensorSerialError}
                                    helperText={weatherSensorSerialErrorMessage}
                                />
                                {weatherSensorSerialFormatWarning && (
                                    <span style={{ color: "orange", fontSize: "0.8em" }}>
                                        Warning: Serial Number should be in the format A41XXXXXX
                                    </span>
                                )}
                            </FormControl>
                            <FormControl>
                                <InputLabel htmlFor="weather-sensor-site-id">Site Number</InputLabel>
                                <TextField
                                    id="weather-sensor-site-id"
                                    type="number"
                                    value={(deviceData as Partial<WeatherSensor>)?.site_id ?? ""}
                                    onChange={(event) => storeChangeInDeviceBeingEdited("site_id", event.target.value)}
                                    onWheel={(event) => event.target instanceof HTMLElement && event.target.blur()}
                                    error={weatherSensorSiteIdError}
                                    helperText={weatherSensorSiteIdErrorMessage}
                                />
                            </FormControl>
                            <FormControl>
                                <InputLabel htmlFor="weather-sensor-type">Device Type</InputLabel>
                                <Select
                                    id="weather-sensor-type"
                                    value={(deviceData as Partial<WeatherSensor>)?.device_type ?? ""}
                                    onChange={(event) =>
                                        storeChangeInDeviceBeingEdited("device_type", event.target.value)
                                    }
                                    error={weatherSensorTypeError}
                                >
                                    {WEATHER_SENSOR_TYPE_OPTIONS.map((option) => (
                                        <MenuItem key={option.value} value={option.value}>
                                            {option.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                                {weatherSensorTypeError && (
                                    <span style={{ color: "red", fontSize: "0.8em" }}>
                                        {weatherSensorTypeErrorMessage}
                                    </span>
                                )}
                            </FormControl>
                            <FormControl>
                                <LocalizationProvider dateAdapter={AdapterDayjs}>
                                    <DateTimeField
                                        label="Install Date (In UTC)"
                                        value={dayjs.utc((deviceData as Partial<WeatherSensor>)?.start_dttm)}
                                        onChange={(newDate) =>
                                            storeChangeInDeviceBeingEdited("start_dttm", newDate?.toISOString())
                                        }
                                        slotProps={{
                                            textField: {
                                                error: weatherSensorInstallDateError,
                                                helperText: weatherSensorInstallDateErrorMessage,
                                            },
                                        }}
                                    />
                                </LocalizationProvider>
                            </FormControl>
                            <FormControl>
                                <LocalizationProvider dateAdapter={AdapterDayjs}>
                                    <DateTimeField
                                        label="End Date (In UTC) - Optional"
                                        value={
                                            (deviceData as Partial<WeatherSensor>)?.end_dttm
                                                ? dayjs.utc((deviceData as Partial<WeatherSensor>)?.end_dttm)
                                                : null
                                        }
                                        onChange={(newDate) =>
                                            storeChangeInDeviceBeingEdited("end_dttm", newDate?.toISOString())
                                        }
                                        slotProps={{
                                            textField: {
                                                error: weatherSensorEndDateError,
                                                helperText: weatherSensorEndDateErrorMessage,
                                            },
                                        }}
                                    />
                                </LocalizationProvider>
                                {(deviceData as Partial<WeatherSensor>)?.end_dttm && !weatherSensorEndDateError && (
                                    <span
                                        style={{
                                            color: "orange",
                                            fontSize: "0.85em",
                                            marginTop: "4px",
                                            display: "block",
                                        }}
                                    >
                                        WARNING: Only use this field to deprecate a sensor WITHOUT replacement (e.g.,
                                        sensor moved to a different location). If replacing at the SAME location (within
                                        breadbox-sized distance), use the Replace Sensor feature instead.
                                    </span>
                                )}
                            </FormControl>
                            <FormControl>
                                <InputLabel htmlFor="height-above-ground">Install Height (meters)</InputLabel>
                                <TextField
                                    id="height-above-ground"
                                    type="number"
                                    value={
                                        // Enforce max of 2 digits after decimal point, following MD schema
                                        (deviceData as Partial<WeatherSensor>)?.height_above_ground
                                            ? +Number(
                                                  (deviceData as Partial<WeatherSensor>).height_above_ground
                                              ).toFixed(2)
                                            : ""
                                    }
                                    onChange={(event) =>
                                        storeChangeInDeviceBeingEdited("height_above_ground", event.target.value)
                                    }
                                    onWheel={(event) => event.target instanceof HTMLElement && event.target.blur()}
                                    error={weatherSensorInstallHeightError}
                                    helperText={weatherSensorInstallHeightErrorMessage}
                                />
                            </FormControl>
                            <FormControl>
                                <InputLabel htmlFor="elevation">Elevation (Derived from Install Height)</InputLabel>
                                <TextField
                                    id="elevation"
                                    type="disabled"
                                    value={(deviceData as Partial<WeatherSensor>)?.elevation ?? "N/A"}
                                />
                            </FormControl>
                            <FormControl>
                                <InputLabel htmlFor="anemometor-heading">Anemometer Heading</InputLabel>
                                <Select
                                    id="anemometer-heading"
                                    value={(deviceData as Partial<WeatherSensor>)?.anemometer_heading ?? ""}
                                    onChange={(event) => {
                                        storeChangeInDeviceBeingEdited("anemometer_heading", event.target.value);
                                    }}
                                >
                                    {CARDINAL_DIRECTIONS.map((option) => (
                                        <MenuItem key={option.value} value={option.value}>
                                            {option.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            <FormControl>
                                <InputLabel htmlFor="mounting-arm-heading">Mounting Arm Heading</InputLabel>
                                <Select
                                    id="mounting-arm-heading"
                                    value={String(
                                        (deviceData as Partial<WeatherSensor>)?.mounting_arm_orientation_heading ?? ""
                                    )}
                                    onChange={(event) => {
                                        const newHeading = event.target.value;

                                        // Map degree value to cardinal label
                                        const degreeToLabel: Record<string, string> = {
                                            "0": "N",
                                            "45": "NE",
                                            "90": "E",
                                            "135": "SE",
                                            "180": "S",
                                            "225": "SW",
                                            "270": "W",
                                            "315": "NW",
                                        };
                                        const labelToDegree: Record<string, string> = {
                                            N: "0",
                                            NE: "45",
                                            E: "90",
                                            SE: "135",
                                            S: "180",
                                            SW: "225",
                                            W: "270",
                                            NW: "315",
                                        };
                                        const opposites: Record<string, string> = {
                                            N: "S",
                                            S: "N",
                                            E: "W",
                                            W: "E",
                                            NE: "SW",
                                            SW: "NE",
                                            NW: "SE",
                                            SE: "NW",
                                        };

                                        // Calculate the opposite heading for anemometer
                                        let cardinal = "";
                                        if (
                                            typeof newHeading === "number" ||
                                            (typeof newHeading === "string" && degreeToLabel[newHeading])
                                        ) {
                                            cardinal = degreeToLabel[String(newHeading)];
                                        } else if (typeof newHeading === "string" && labelToDegree[newHeading]) {
                                            cardinal = newHeading;
                                        }
                                        const oppositeCardinal = opposites[cardinal];
                                        const oppositeDegree = labelToDegree[oppositeCardinal];

                                        // Update both fields in a single dispatch to avoid race conditions
                                        if (!deviceData) {
                                            return;
                                        }

                                        const updatedData = {
                                            ...deviceData,
                                            mounting_arm_orientation_heading: Number(newHeading),
                                            ...(oppositeDegree ? { anemometer_heading: Number(oppositeDegree) } : {}),
                                        };

                                        dispatch(setCurrentDeviceBeingEdited(updatedData));
                                    }}
                                    error={weatherSensorMountingArmHeadingError}
                                >
                                    {CARDINAL_DIRECTIONS.map((option) => (
                                        <MenuItem key={option.value} value={option.value}>
                                            {option.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                                {weatherSensorMountingArmHeadingError && (
                                    <span style={{ color: "red", fontSize: "0.8em" }}>
                                        {weatherSensorMountingArmHeadingErrorMessage}
                                    </span>
                                )}
                            </FormControl>
                            <FormControl>
                                <InputLabel htmlFor="mounting-arm-length">Mounting Arm Length (meters)</InputLabel>
                                <TextField
                                    id="mounting-arm-length"
                                    type="number"
                                    value={(deviceData as Partial<WeatherSensor>)?.mounting_arm_length ?? ""}
                                    onChange={(event) =>
                                        storeChangeInDeviceBeingEdited("mounting_arm_length", event.target.value)
                                    }
                                    onWheel={(event) => event.target instanceof HTMLElement && event.target.blur()}
                                />
                                {weatherSensorMountingArmLengthWarning && (
                                    <span style={{ color: "orange", fontSize: "0.8em" }}>
                                        Warning: Mounting Arm Length should be 0.91 meters
                                    </span>
                                )}
                            </FormControl>
                            <div>
                                <Checkbox
                                    checked={skipMasterdataSync}
                                    onChange={(event) => handleSkipSyncChange(event.target.checked)}
                                />
                                Skip sync to masterdata (FOR INTERNAL USE ONLY)
                                {skipMasterdataSync && (
                                    <span style={{ color: "red", fontSize: "0.9em", marginLeft: "10px" }}>
                                        ⚠️ SYNC DISABLED - Data will not be sent to Masterdata
                                    </span>
                                )}
                            </div>
                            <div className={classes.formButtons}>
                                <Button onClick={() => dispatch(stopEditingDevice())}>Cancel</Button>
                                <Button
                                    onClick={() => onSubmitWeatherSensorForm()}
                                    disabled={
                                        weatherSensorSerialError ||
                                        weatherSensorSiteIdError ||
                                        weatherSensorTypeError ||
                                        weatherSensorMountingArmHeadingError ||
                                        weatherSensorInstallDateError ||
                                        weatherSensorInstallHeightError ||
                                        weatherSensorEndDateError
                                    }
                                >
                                    Save
                                </Button>
                            </div>

                            {deviceUpdateStatus === "failed" && deviceUpdateError && (
                                <ErrorText error={deviceUpdateError} />
                            )}
                        </>
                    )}
                </>
            )}
        </div>
    );
}
